import { useEffect, useMemo, useState } from 'react';
import { Plus, Trash2, Pencil, FlaskConical } from 'lucide-react';
import type { Reader, ReaderDraft, ReaderField } from '../lib/types';
import { deleteReader, emptyReaderDraft, updateReader } from '../services/readers';
import { Badge, Button, Input, Modal, cx } from './ui';
import NewReaderSetup from './NewReaderSetup';
import type { LearnedReader } from '../services/readings';

/**
 * Defining what you can read.
 *
 * The app used to know about exactly two instruments. This is where that stops
 * being true: a reader is a name and a list of fields, and everything
 * downstream — what the model is asked to find, what the confirmation screen
 * offers, which columns the export carries — is generated from it.
 *
 * Your readers are yours. Other people's appear lower down, read-only, so their
 * results can be understood without their instruments becoming yours.
 *
 * The form deliberately asks for very little. A label is the only required
 * thing per field; units, decimals and a plausible range are refinements, and
 * the aliases box exists because a lab's own column headings beat anything this
 * app could guess at.
 */
export interface UsedReader {
  id: string;
  name: string;
  fields: { key: string; label: string; unit?: string }[];
  /** Who captured with it, so "not mine" has a face on it. */
  byName?: string | null;
}

export default function ReaderManager({
  open,
  onClose,
  uid,
  readers,
  used,
  projectId,
  onReview,
}: {
  open: boolean;
  onClose: () => void;
  /** Readers belong to a person. This is whose. */
  uid: string;
  readers: Reader[];
  /**
   * Readers other people have captured with in this project, reconstructed
   * from the snapshot each capture carries. Visible so results can be
   * understood; not usable, because a reader is a description of someone
   * else's bench.
   */
  used: UsedReader[];
  /** Where a new reader's trial photo is kept until it is reviewed. */
  projectId: string;
  onReview: (captureId: string, learned: LearnedReader) => void;
}) {
  const [editing, setEditing] = useState<Reader | null>(null);
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<Reader | null>(null);
  const mine = new Set(readers.map((r) => r.id));
  const theirs = used.filter((u) => !mine.has(u.id));

  return (
    <>
      <Modal open={open} onClose={onClose} title="Readers" wide>
        <p className="mb-4 text-sm text-ink-400">
          A reader is an instrument as you actually use it — what it measures, and what those
          columns are called on the sheet it produces. Readers are yours: they follow you across
          every project, and nobody else sees them in their app.
        </p>

        {readers.length === 0 ? (
          <p className="rounded-xl border border-ink-700 bg-ink-900 p-6 text-center text-sm text-ink-400">
            No readers yet.
          </p>
        ) : (
          <ul className="space-y-2">
            {readers.map((r) => (
              <li key={r.id} className="rounded-lg border border-ink-700 bg-ink-800/40 p-3">
                <div className="flex items-start gap-2">
                  <FlaskConical size={15} className="mt-0.5 shrink-0 text-ink-400" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-medium">{r.name || 'Untitled reader'}</span>
                      {r.builtIn && <Badge tone="neutral">built in</Badge>}
                      <Badge tone="info">
                        {r.fields.length} field{r.fields.length === 1 ? '' : 's'}
                      </Badge>
                    </div>
                    {r.description && (
                      <p className="mt-0.5 text-xs text-ink-400">{r.description}</p>
                    )}
                    <p className="mt-1.5 flex flex-wrap gap-1.5">
                      {r.fields.map((f) => (
                        <span
                          key={f.key}
                          className="rounded bg-ink-800 px-1.5 py-0.5 font-mono text-[11px] text-ink-300"
                        >
                          {f.label}
                          {f.unit && <span className="text-ink-500"> {f.unit}</span>}
                        </span>
                      ))}
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-1">
                      <button
                        onClick={() => setEditing(r)}
                        className="icon-btn text-ink-400 hover:bg-ink-800"
                        aria-label={`Edit ${r.name}`}
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => setConfirmDelete(r)}
                        className="icon-btn text-ink-400 hover:bg-red-950 hover:text-red-300"
                        aria-label={`Delete ${r.name}`}
                      >
                        <Trash2 size={14} />
                      </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        <Button
          className="mt-4"
          variant="primary"
          icon={<Plus size={16} />}
          onClick={() => setCreating(true)}
        >
          New reader
        </Button>

        {theirs.length > 0 && (
          <section className="mt-7 border-t border-ink-800 pt-5">
            <h3 className="field-label mb-1">Used in this project by others</h3>
            <p className="mb-3 text-xs text-ink-400">
              Shown so the readings make sense. You cannot capture with someone else{String.fromCharCode(8217)}s
              reader — build your own with the fields you need.
            </p>
            <ul className="space-y-2">
              {theirs.map((u) => (
                <li
                  key={u.id}
                  className="rounded-lg border border-dashed border-ink-700 bg-ink-800/20 p-3"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <FlaskConical size={14} className="shrink-0 text-ink-500" />
                    <span className="text-sm font-medium text-ink-300">{u.name}</span>
                    {u.byName && <Badge tone="neutral">{u.byName}</Badge>}
                  </div>
                  <p className="mt-1.5 flex flex-wrap gap-1.5">
                    {u.fields.map((f) => (
                      <span
                        key={f.key}
                        className="rounded bg-ink-800 px-1.5 py-0.5 font-mono text-[11px] text-ink-400"
                      >
                        {f.label}
                        {f.unit ? <span className="text-ink-600"> {f.unit}</span> : null}
                      </span>
                    ))}
                  </p>
                </li>
              ))}
            </ul>
          </section>
        )}
      </Modal>

      <ReaderEditor
        open={editing !== null}
        reader={editing}
        onSubmit={async (draft) => {
          if (editing) await updateReader(uid, editing.id, draft);
        }}
        onClose={() => setEditing(null)}
      />

      {/* Same trial-photo setup as a newer project, but the reader only joins
          the person's own list: an older project has no reader list to add to. */}
      <NewReaderSetup
        open={creating}
        onClose={() => setCreating(false)}
        projectId={projectId}
        chosen={[]}
        addToProject={false}
        onReview={(captureId, learned) => {
          onClose();
          onReview(captureId, learned);
        }}
      />

      <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(null)} title="Delete reader">
        <p className="text-sm text-ink-300">
          Delete <strong className="text-ink-100">{confirmDelete?.name}</strong>? Readings already
          recorded with it are kept — this only removes it as a choice for new captures.
        </p>
        <div className="mt-4 flex justify-end gap-2">
          <Button variant="ghost" onClick={() => setConfirmDelete(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={async () => {
              if (confirmDelete) await deleteReader(uid, confirmDelete.id);
              setConfirmDelete(null);
            }}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </>
  );
}

/**
 * The reader form, on its own so a project can use it too: a project edits its
 * own copy of a reader, an account edits its personal one, and the form does
 * not need to know which. `onSubmit` does the saving.
 */
export function ReaderEditor({
  open,
  reader,
  onSubmit,
  onClose,
}: {
  open: boolean;
  reader: Pick<Reader, 'name' | 'description' | 'fields'> & { builtIn?: boolean } | null;
  onSubmit: (draft: ReaderDraft) => Promise<void>;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState<ReaderDraft>(emptyReaderDraft);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setDraft(
      reader
        ? {
            name: reader.name,
            description: reader.description,
            // Copied, not referenced: editing must not mutate what is on screen
            // behind this dialog before anything is saved.
            fields: reader.fields.map((f) => ({ ...f, aliases: [...f.aliases] })),
          }
        : emptyReaderDraft(),
    );
  }, [open, reader]);

  const setField = (i: number, patch: Partial<ReaderField>) =>
    setDraft((d) => ({
      ...d,
      fields: d.fields.map((f, n) => (n === i ? { ...f, ...patch } : f)),
    }));

  const named = useMemo(() => draft.fields.filter((f) => f.label.trim()).length, [draft.fields]);
  const canSave = draft.name.trim().length > 0 && named > 0 && !busy;

  async function save() {
    if (!canSave) return;
    setBusy(true);
    setError(null);
    try {
      await onSubmit(draft);
      onClose();
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not save that reader.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title={reader ? 'Edit reader' : 'New reader'} wide>
      <div className="space-y-4">
        <div>
          <label className="field-label mb-1.5 block" htmlFor="r-name">
            Name
          </label>
          <Input
            id="r-name"
            value={draft.name}
            onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
            placeholder=""
          />
        </div>

        <div>
          <p className="field-label mb-2">Values it reads</p>

          {/* Only what a person would want to correct: what each value is called
              and its unit. Headings, decimals and ranges come from the trial
              photo and the reads after it. */}
          <ul className="space-y-2">
            {draft.fields.map((f, i) => (
              <li key={i} className="flex items-end gap-2">
                <div className="min-w-0 flex-1">
                  {i === 0 && <label className="field-label mb-1 block">Value</label>}
                  <Input
                    value={f.label}
                    onChange={(e) => setField(i, { label: e.target.value })}
                    aria-label={`Name of value ${i + 1}`}
                  />
                </div>
                <div className="w-24 shrink-0">
                  {i === 0 && <label className="field-label mb-1 block">Unit</label>}
                  <Input
                    value={f.unit}
                    onChange={(e) => setField(i, { unit: e.target.value })}
                    aria-label={`Unit of value ${i + 1}`}
                  />
                </div>
                <button
                  onClick={() =>
                    setDraft((d) => ({ ...d, fields: d.fields.filter((_, n) => n !== i) }))
                  }
                  disabled={draft.fields.length === 1}
                  className={cx(
                    'icon-btn shrink-0 text-ink-400 hover:bg-red-950 hover:text-red-300',
                    draft.fields.length === 1 && 'invisible',
                  )}
                  aria-label={`Remove ${f.label || 'value'}`}
                >
                  <Trash2 size={14} />
                </button>
              </li>
            ))}
          </ul>
        </div>

        {error && (
          <p className="rounded-lg border border-red-800 bg-red-950 p-2.5 text-xs text-red-200">
            {error}
          </p>
        )}

        {reader?.builtIn && (
          <p className="rounded-lg border border-ink-700 bg-ink-800/40 p-2.5 text-xs text-ink-400">
            This reader came with the app. Editing it is fine — readings already recorded keep the
            values they were saved with.
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} disabled={!canSave} onClick={() => void save()}>
            {reader ? 'Save reader' : 'Create reader'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
